import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import HexagonOutlinedIcon from '@mui/icons-material/HexagonOutlined';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Marker,
  Source,
  Layer,
  type MapRef,
  type MarkerDragEvent,
} from '@vis.gl/react-maplibre';
import type { MapMouseEvent } from 'maplibre-gl';
import MapView from 'components/Map/MapView';
import { useMapFlyTo } from 'hooks/useMapFlyTo';
import { latLonToUtm, utmToLatLon } from 'utils/utmToLatLon';
import { useAppSelector } from 'store/hook';
import { selectLatestTagData } from 'store/device.slice';
import { selectAssets } from 'store/asset.slice';
import { images } from 'components/Asset/PNG';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';

function buildTruckLabel(bumperNumber: string, ignitionOn: boolean): string {
  const canvas = document.createElement('canvas');
  canvas.width = 150;
  canvas.height = 34;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const dotColor = ignitionOn ? '#4ade80' : '#f87171';
  ctx.fillStyle = 'rgba(10,15,30,0.88)';
  ctx.strokeStyle = ignitionOn ? '#4ade80' : '#94a3b8';
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(canvas.width - r, 0);
  ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
  ctx.lineTo(canvas.width, canvas.height - r);
  ctx.quadraticCurveTo(
    canvas.width,
    canvas.height,
    canvas.width - r,
    canvas.height
  );
  ctx.lineTo(r, canvas.height);
  ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = dotColor;
  ctx.beginPath();
  ctx.arc(11, canvas.height / 2, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(bumperNumber, 20, canvas.height / 2);
  return canvas.toDataURL();
}

function getAssetPng(assetType: string): string {
  const svgKey = mapVehiclesAssets(assetType);
  const pngKey =
    svgKey === 'haulTruckImage' ||
    svgKey === 'lightTruckImage' ||
    svgKey === 'heavyTruckImage'
      ? 'fallbackhaul_truckImageSmall'
      : 'fallbackloaderImageSmall';
  return images[pngKey] ?? '';
}

export interface PolygonVertex {
  lat: number;
  lon: number;
  easting: number;
  northing: number;
}

interface TruckDot {
  deviceId: string;
  lat: number;
  lon: number;
  bumper: string;
  ignitionOn: boolean;
  assetType: string;
}

interface BoundaryMapPickerProps {
  onDraw: (vertices: PolygonVertex[]) => void;
  locationEasting?: number;
  locationNorthing?: number;
  centroidLat?: number | null;
  centroidLon?: number | null;
}

// The old Cesium drag handling hit-tested every vertex within this pixel
// radius of the cursor to find the "nearest" one to grab. MapLibre `Marker`s
// are real DOM elements, so dragging now targets the exact marker under the
// pointer instead of a nearest-neighbour search — this constant now only
// pads each vertex's invisible hit area (in drag mode) so it stays an easy,
// forgiving grab target rather than shrinking to the visible dot's size.
const DRAG_HIT_PADDING_PX = 20;

// Zoom levels replacing the old flyTo altitudes (metres) — there is no direct
// altitude→zoom formula, these were picked to match the old visual closeness.
const LOCATION_FOCUS_ZOOM = 18;
const SITE_FOCUS_ZOOM = 16;

function buildLocationPin(): string {
  const canvas = document.createElement('canvas');
  canvas.width = 32;
  canvas.height = 46;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const cx = 16;
  const r = 13;
  ctx.fillStyle = '#e53935';
  ctx.beginPath();
  ctx.arc(cx, r, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - 6, r + 9);
  ctx.quadraticCurveTo(cx - 3, 43, cx, 46);
  ctx.quadraticCurveTo(cx + 3, 43, cx + 6, r + 9);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(cx, r, 6, 0, Math.PI * 2);
  ctx.fill();
  return canvas.toDataURL();
}

const LOCATION_PIN_IMAGE = buildLocationPin();

const BoundaryMapPickerInner = ({
  onDraw,
  locationEasting,
  locationNorthing,
  centroidLat,
  centroidLon,
}: BoundaryMapPickerProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customerCode } = useCustomerInfo();
  const mapRef = useRef<MapRef | null>(null);
  const hasCenteredRef = useRef(false);
  const { flyTo } = useMapFlyTo(mapRef);

  const [mapReady, setMapReady] = useState(false);
  const [vertices, setVertices] = useState<PolygonVertex[]>([]);
  const [dragMode, setDragMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Ref so the map click handler always sees the fresh mode without
  // re-attaching the listener on every toggle.
  const dragModeRef = useRef(false);
  useEffect(() => {
    dragModeRef.current = dragMode;
  }, [dragMode]);

  const latestTagData = useAppSelector(selectLatestTagData);
  const assets = useAppSelector(selectAssets);
  const { proj4String, localOriginEasting, localOriginNorthing } =
    useAppSelector((s) => s.persistedReducer.customer);

  const assetInfoMap = useMemo<
    Record<string, { bumper: string; assetType: string }>
  >(() => {
    const map: Record<string, { bumper: string; assetType: string }> = {};
    assets.forEach((a) => {
      if (a.device?.deviceId) {
        map[a.device.deviceId] = {
          bumper: a.bumperNumber ?? a.device.deviceId,
          assetType: a.assetType ?? 'haul_truck',
        };
      }
    });
    return map;
  }, [assets]);

  const truckDots = useMemo<TruckDot[]>(() => {
    return Object.entries(latestTagData)
      .map(([deviceId, s]) => {
        const lat = Number(s.gps_latitude);
        const lon = Number(s.gps_longitude);
        if (!isFinite(lat) || !isFinite(lon) || lat === 0 || lon === 0)
          return null;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
        const info = assetInfoMap[deviceId];
        return {
          deviceId,
          lat,
          lon,
          bumper: info?.bumper ?? deviceId,
          ignitionOn: String(s.sb_ignition_status) === 'true',
          assetType: info?.assetType ?? 'haul_truck',
        };
      })
      .filter((d): d is TruckDot => d !== null);
  }, [latestTagData, assetInfoMap]);

  const mineSiteCenter = useMemo<{ lat: number; lon: number } | null>(() => {
    if (truckDots.length === 0) return null;
    const avgLat = truckDots.reduce((s, d) => s + d.lat, 0) / truckDots.length;
    const avgLon = truckDots.reduce((s, d) => s + d.lon, 0) / truckDots.length;
    return { lat: avgLat, lon: avgLon };
  }, [truckDots]);

  const locationLatLon = useMemo(() => {
    // Prefer centroid coords — reject only 0,0 (DB "unset"), not individual zeros
    if (
      centroidLat != null &&
      centroidLon != null &&
      Number.isFinite(centroidLat) &&
      Number.isFinite(centroidLon) &&
      !(centroidLat === 0 && centroidLon === 0)
    ) {
      return { latitude: centroidLat, longitude: centroidLon };
    }
    // Fall back to UTM → WGS84 conversion from easting/northing
    if (!locationEasting || !locationNorthing) return null;
    return utmToLatLon(
      locationEasting,
      locationNorthing,
      proj4String,
      localOriginEasting,
      localOriginNorthing
    );
  }, [
    centroidLat,
    centroidLon,
    locationEasting,
    locationNorthing,
    proj4String,
    localOriginEasting,
    localOriginNorthing,
  ]);

  const primaryColorHex = theme.palette.primary.main;

  const addVertex = useCallback(
    (lat: number, lon: number) => {
      const utm = latLonToUtm(
        lat,
        lon,
        proj4String,
        localOriginEasting,
        localOriginNorthing
      );
      if (!utm) return;
      setVertices((prev) => [...prev, { lat, lon, ...utm }]);
    },
    [proj4String, localOriginEasting, localOriginNorthing]
  );

  // Draw mode: click adds a vertex — replaces the Cesium LEFT_CLICK handler
  // (pickEllipsoid + Cartographic conversion) with MapLibre's native click
  // event, which already hands back lon/lat directly.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    const handleClick = (e: MapMouseEvent) => {
      if (dragModeRef.current) return;
      addVertex(e.lngLat.lat, e.lngLat.lng);
    };
    map.on('click', handleClick);
    return () => {
      map.off('click', handleClick);
    };
  }, [mapReady, addVertex]);

  const handleVertexDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleVertexDrag = useCallback(
    (idx: number, e: MarkerDragEvent) => {
      const { lat, lng } = e.lngLat;
      const utm = latLonToUtm(
        lat,
        lng,
        proj4String,
        localOriginEasting,
        localOriginNorthing
      );
      if (!utm) return;
      setVertices((prev) => {
        if (idx >= prev.length) return prev;
        const updated = [...prev];
        updated[idx] = { lat, lon: lng, ...utm };
        return updated;
      });
    },
    [proj4String, localOriginEasting, localOriginNorthing]
  );

  const handleVertexDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!mapReady || hasCenteredRef.current) return;
    if (locationLatLon) {
      hasCenteredRef.current = true;
      flyTo(
        locationLatLon.latitude,
        locationLatLon.longitude,
        LOCATION_FOCUS_ZOOM
      );
      return;
    }
    if (mineSiteCenter) {
      hasCenteredRef.current = true;
      flyTo(mineSiteCenter.lat, mineSiteCenter.lon, SITE_FOCUS_ZOOM);
    }
  }, [mapReady, locationLatLon, mineSiteCenter, flyTo]);

  const truckMarkers = useMemo(() => {
    return truckDots.map((dot) => {
      const labelImage = buildTruckLabel(dot.bumper, dot.ignitionOn);
      const pngImage = getAssetPng(dot.assetType);
      return (
        <Marker
          key={dot.deviceId}
          longitude={dot.lon}
          latitude={dot.lat}
          anchor="center"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={labelImage}
              sx={{ height: 17, mb: '2px' }}
            />
            <Box component="img" src={pngImage} sx={{ height: 28 }} />
          </Box>
        </Marker>
      );
    });
  }, [truckDots]);

  // Vertex markers: in drag mode make them slightly larger as grab handles,
  // draggable, and given a generous invisible hit-padding; in draw mode they
  // are purely visual (pointer-events disabled) so a click lands on the map
  // underneath and adds a new vertex, exactly like the old scene-pick did
  // regardless of what was rendered at that pixel.
  const vertexMarkers = useMemo(() => {
    return vertices.map((v, i) => {
      const dotSize = dragMode ? (i === 0 ? 14 : 11) : i === 0 ? 12 : 9;
      const hitSize = dotSize + DRAG_HIT_PADDING_PX;
      return (
        <Marker
          key={`vertex-${i}`}
          longitude={v.lon}
          latitude={v.lat}
          anchor="center"
          draggable={dragMode}
          style={{
            pointerEvents: dragMode ? 'auto' : 'none',
            cursor: dragMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
          onDragStart={handleVertexDragStart}
          onDrag={(e) => {
            handleVertexDrag(i, e);
          }}
          onDragEnd={handleVertexDragEnd}
        >
          <Box
            sx={{
              width: hitSize,
              height: hitSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: dotSize,
                height: dotSize,
                borderRadius: '50%',
                bgcolor: '#fff',
                border: `${dragMode ? 3 : 2.5}px solid ${primaryColorHex}`,
              }}
            />
          </Box>
        </Marker>
      );
    });
  }, [
    vertices,
    dragMode,
    isDragging,
    primaryColorHex,
    handleVertexDragStart,
    handleVertexDrag,
    handleVertexDragEnd,
  ]);

  // Draft polygon-in-progress: one GeoJSON source with up to 3 features
  // (fill, main outline, dim closing segment) — replaces the Cesium
  // polygon/polyline Entities.
  const draftGeoJson = useMemo(() => {
    const features: Array<{
      type: 'Feature';
      properties: { kind: string };
      geometry: { type: 'Polygon' | 'LineString'; coordinates: any };
    }> = [];

    if (vertices.length >= 3) {
      const ring = vertices.map((v) => [v.lon, v.lat]);
      ring.push(ring[0]);
      features.push({
        type: 'Feature',
        properties: { kind: 'fill' },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
    }
    if (vertices.length >= 2) {
      features.push({
        type: 'Feature',
        properties: { kind: 'line' },
        geometry: {
          type: 'LineString',
          coordinates: vertices.map((v) => [v.lon, v.lat]),
        },
      });
    }
    if (vertices.length >= 3) {
      const last = vertices[vertices.length - 1];
      const first = vertices[0];
      features.push({
        type: 'Feature',
        properties: { kind: 'closing' },
        geometry: {
          type: 'LineString',
          coordinates: [
            [last.lon, last.lat],
            [first.lon, first.lat],
          ],
        },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }, [vertices]);

  const isClosed = vertices.length >= 3;
  const lastVertex = vertices.length > 0 ? vertices[vertices.length - 1] : null;

  const cursor = dragMode ? (isDragging ? 'grabbing' : 'grab') : 'crosshair';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
      {/* ── Toolbar ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Left: mode toggle + status chip */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={dragMode ? 'drag' : 'draw'}
            exclusive
            onChange={(_, val) => {
              if (val !== null) setDragMode(val === 'drag');
            }}
            size="small"
            sx={{
              height: 28,
              '& .MuiToggleButton-root': {
                px: 1.2,
                py: 0,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'none',
                gap: 0.5,
                border: '1px solid',
                borderColor: (th) => alpha(th.palette.divider, 0.6),
              },
            }}
          >
            <ToggleButton value="draw">
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
              {t('boundary_map_mode_draw')}
            </ToggleButton>
            <ToggleButton value="drag">
              <OpenWithIcon sx={{ fontSize: 14 }} />
              {t('boundary_map_mode_drag')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Chip
            icon={<HexagonOutlinedIcon sx={{ fontSize: 13 }} />}
            label={
              vertices.length === 0
                ? t('boundary_map_hint_click')
                : vertices.length < 3
                ? `${vertices.length} ${t('boundary_map_vertices')} · ${t(
                    'boundary_map_need_min'
                  )}`
                : `${vertices.length} ${t('boundary_map_vertices')}`
            }
            size="small"
            variant="outlined"
            color={isClosed ? 'primary' : 'default'}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
        </Box>

        {/* Right: action buttons */}
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          <Button
            size="small"
            startIcon={<UndoIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setVertices((prev) => prev.slice(0, -1));
            }}
            disabled={vertices.length === 0}
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              borderRadius: '6px',
              px: 1.2,
              py: 0.3,
              minWidth: 0,
              color: 'text.secondary',
            }}
          >
            {t('boundary_map_undo')}
          </Button>
          <Button
            size="small"
            color="error"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setVertices([]);
            }}
            disabled={vertices.length === 0}
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              borderRadius: '6px',
              px: 1.2,
              py: 0.3,
              minWidth: 0,
            }}
          >
            {t('boundary_map_clear')}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              onDraw(vertices);
            }}
            disabled={!isClosed}
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              borderRadius: '6px',
              px: 1.5,
              py: 0.3,
              minWidth: 0,
            }}
          >
            {t('boundary_map_confirm')}
          </Button>
        </Box>
      </Box>

      {/* ── Map ── */}
      <Box
        sx={{
          height: 400,
          position: 'relative',
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: (th) =>
            isClosed
              ? alpha(th.palette.primary.main, 0.4)
              : alpha(th.palette.divider, 0.5),
          cursor,
          transition: 'border-color 0.2s',
        }}
      >
        {!mapReady && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.default',
              zIndex: 5,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        <MapView
          ref={mapRef}
          basemap={
            hasNoSatelliteCoverage(customerCode) ? undefined : 'satellite'
          }
          style={{ width: '100%', height: '100%' }}
          onLoad={() => {
            setMapReady(true);
          }}
        >
          {locationLatLon && (
            <Marker
              longitude={locationLatLon.longitude}
              latitude={locationLatLon.latitude}
              anchor="bottom"
            >
              <Box
                component="img"
                src={LOCATION_PIN_IMAGE}
                sx={{ height: 46, pointerEvents: 'none' }}
              />
            </Marker>
          )}

          {truckMarkers}

          {draftGeoJson.features.length > 0 && (
            <Source id="boundary-draft" type="geojson" data={draftGeoJson}>
              <Layer
                id="boundary-draft-fill"
                type="fill"
                filter={['==', ['get', 'kind'], 'fill']}
                paint={{ 'fill-color': primaryColorHex, 'fill-opacity': 0.14 }}
              />
              <Layer
                id="boundary-draft-line"
                type="line"
                filter={['==', ['get', 'kind'], 'line']}
                paint={{
                  'line-color': primaryColorHex,
                  'line-width': 2.5,
                  'line-opacity': 0.9,
                }}
              />
              <Layer
                id="boundary-draft-closing"
                type="line"
                filter={['==', ['get', 'kind'], 'closing']}
                paint={{
                  'line-color': primaryColorHex,
                  'line-width': 1.5,
                  'line-opacity': 0.4,
                }}
              />
            </Source>
          )}

          {vertexMarkers}
        </MapView>

        {/* Mode hint */}
        {mapReady && vertices.length === 0 && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 2,
              py: 0.6,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.88),
              backdropFilter: 'blur(6px)',
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.4),
              zIndex: 4,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('boundary_map_draw_hint')}
            </Typography>
          </Box>
        )}

        {/* Drag mode hint */}
        {mapReady && dragMode && vertices.length > 0 && !isDragging && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 2,
              py: 0.6,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.88),
              backdropFilter: 'blur(6px)',
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.4),
              zIndex: 4,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
            }}
          >
            <OpenWithIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography variant="caption" color="text.secondary">
              {t('boundary_map_drag_hint')}
            </Typography>
          </Box>
        )}

        {/* Coords badge */}
        {lastVertex && !dragMode && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              px: 1.8,
              py: 0.5,
              borderRadius: 2,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.9),
              backdropFilter: 'blur(6px)',
              border: '1px solid',
              borderColor: (th) =>
                isClosed
                  ? alpha(th.palette.primary.main, 0.4)
                  : alpha(th.palette.divider, 0.4),
              zIndex: 4,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.7rem',
              }}
            >
              E {lastVertex.easting.toFixed(1)} · N{' '}
              {lastVertex.northing.toFixed(1)}
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ fontSize: '0.66rem' }}
            >
              pt {vertices.length}
            </Typography>
          </Box>
        )}

        {/* Start vertex indicator */}
        {vertices.length > 0 && mapReady && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 4,
              px: 1,
              py: 0.4,
              borderRadius: 1.5,
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.3),
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'white',
                border: (th) => `2px solid ${th.palette.primary.main}`,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              color="primary.main"
              fontWeight={600}
              sx={{ fontSize: '0.7rem' }}
            >
              {t('boundary_map_start')}
            </Typography>
          </Box>
        )}

        {/* Truck count badge */}
        {truckDots.length > 0 && mapReady && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 4,
              px: 1,
              py: 0.4,
              borderRadius: 1.5,
              bgcolor: (th) => alpha(th.palette.background.paper, 0.88),
              backdropFilter: 'blur(4px)',
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.divider, 0.4),
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {truckDots.length}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export const BoundaryMapPicker = React.memo(BoundaryMapPickerInner);
